import React, { useMemo } from "react";

import {
  Button,
  Modal,
  Grid,
  Text,
  Divider,
  RadioController,
  Toast,
  Icons,
} from "@pushpress/shared-components";
import { useForm, useWatch } from "react-hook-form";
import {
  getWeekDay,
  removeTimezoneFromDateString,
} from "../../../../utils/dateUtils";
import { capitalizeFirstLetter } from "../../../../utils/misc";
import { ClassItemForEditFragment } from "../CalendarForm-generated.hooks";
import { format } from "date-fns";
import { getClassItemInfo } from "./ConfirmationModal.utils";

export type CalendarItemConfirmationScope = "this" | "all of weekday" | "all";

interface CalendarItemConfirmationForm {
  type: CalendarItemConfirmationScope;
}

interface CalendarItemFormConfirmationModalProps {
  classItem?: ClassItemForEditFragment;
  title: string;
  visible: boolean;
  onClose?: VoidFunction;
  onScopeSelected: (value: CalendarItemConfirmationScope) => void;
  loading?: boolean;
}

function CalendarItemFormConfirmationModal(
  props: CalendarItemFormConfirmationModalProps,
) {
  return (
    <Modal visible={props.visible} closeModal={props.onClose} disableClickAway>
      <CalendarItemFormConfirmation {...props} />
    </Modal>
  );
}

const CalendarItemFormConfirmation = ({
  classItem,
  title,
  onClose,
  onScopeSelected,
  loading,
}: CalendarItemFormConfirmationModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<CalendarItemConfirmationForm>({
    defaultValues: {
      type: "this",
    },
  });

  const type = useWatch({
    control,
    name: "type",
  });

  const onSubmit = (values: CalendarItemConfirmationForm) => {
    onScopeSelected(values.type);
  };

  const weekday = useMemo(() => {
    const startDate = format(
      removeTimezoneFromDateString(classItem?.startDatetime),
      "yyyy-MM-dd",
    );
    return capitalizeFirstLetter(getWeekDay(startDate).toLowerCase());
  }, [classItem]);

  const infoMessage = useMemo(() => {
    return getClassItemInfo(type, classItem);
  }, [type, classItem]);

  return (
    <div data-cy="confirmation-modal">
      <Modal.Card>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          paddingX={3}
          paddingY={3}
        >
          <Grid item>
            <Text variant="heading-4">{title}</Text>
          </Grid>
          <Grid item>
            <Grid container>
              <Grid item>
                <Button
                  text="Close"
                  buttonType="secondary"
                  size="medium"
                  onClick={onClose}
                />
              </Grid>
              <Grid item>
                <Button
                  text="Save"
                  buttonType="primary"
                  size="medium"
                  type="button"
                  loading={loading}
                  disabled={!isValid}
                  onClick={handleSubmit(onSubmit)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Divider />
        <Modal.Content>
          <RadioController
            className="mt-4"
            options={[
              {
                label: `This class`,
                value: "this",
              },
              {
                label: `This and all ${weekday} classes`,
                value: "all of weekday",
              },
              {
                label: "This and all future classes",
                value: "all",
              },
            ]}
            direction="vertical"
            control={control}
            name="type"
            label="Confirmation radio"
            hideLabel
            required
          />
          <Toast
            title={infoMessage}
            message=""
            icon={Icons.Info}
            iconColor="success"
          />
        </Modal.Content>
      </Modal.Card>
    </div>
  );
};

export default CalendarItemFormConfirmationModal;
